/**
 * Componente Dropdown para cambiar el idioma de la aplicación
 *
 * Muestra un menú desplegable con todos los idiomas disponibles.
 * El usuario puede ver la bandera actual y seleccionar otro idioma.
 *
 * El idioma seleccionado se guarda en localStorage automáticamente.
 */

import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Tipo para los idiomas
interface Language {
  code: string;
  name: string;
  flag: string;
}

// Lista de idiomas disponibles
const languages: Language[] = [
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
];

// Idioma por defecto (español)
const defaultLanguage = languages[0];

export function LanguageSelector() {
  // Hook de i18next
  const { i18n } = useTranslation();

  // Obtener el idioma actual
  const currentLang = languages.find((lang) => lang.code === i18n.language) ?? defaultLanguage;

  // Función para cambiar idioma
  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem("lims-language", langCode);
  };

  return (
    <DropdownMenu>
      {/* Botón que muestra la bandera actual */}
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 gap-1 px-2">
          <span className="text-lg">{currentLang.flag}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
          <span className="sr-only">Cambiar idioma</span>
        </Button>
      </DropdownMenuTrigger>

      {/* Menú desplegable con todos los idiomas */}
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className="flex items-center gap-2 cursor-pointer"
          >
            {/* Bandera */}
            <span className="text-lg">{lang.flag}</span>

            {/* Nombre del idioma */}
            <span>{lang.name}</span>

            {/* Check si es el idioma actual */}
            {lang.code === i18n.language && <Check className="h-4 w-4 ml-auto text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
